/**
 * Status, filter, and display config for levels.
 */
import { School, BookOpen, GraduationCap, CheckCircle2, XCircle } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { BadgeVariant } from '@/components/common/LabelBadge';

/** Badge + icon config for a school cycle. */
export interface LevelCycleConfig {
  label: string;
  icon: LucideIcon;
  badgeVariant: BadgeVariant;
  badgeColor: string;
  colorName: 'color-1' | 'color-2' | 'color-3';
}

/** Badge + icon config for a level status. */
export interface LevelStatusConfig {
  label: string;
  icon: LucideIcon;
  badgeVariant: BadgeVariant;
  badgeColor?: string;
}

/** Level list filter state. */
export interface LevelFilters {
  cycles: string[];
  statuses: string[];
  searchTerm?: string;
  minAge?: number;
  maxAge?: number;
}

/** Filter group IDs for the levels {@link DataTable} configure panel. */
export type FilterGroupId = 'filter-cycle' | 'filter-status' | 'filter-search' | 'filter-age-range';

/** Badge config for each school cycle. */
export const LEVEL_CYCLE_CONFIG: Record<string, LevelCycleConfig> = {
  primary: {
    label: 'Primaire',
    icon: School,
    badgeVariant: 'outlined',
    badgeColor: 'var(--color-2-nuance-1)',
    colorName: 'color-2',
  },
  middle: {
    label: 'Collège',
    icon: BookOpen,
    badgeVariant: 'outlined',
    badgeColor: 'var(--color-1-nuance-1)',
    colorName: 'color-1',
  },
  high: {
    label: 'Lycée',
    icon: GraduationCap,
    badgeVariant: 'outlined',
    badgeColor: 'var(--color-3-nuance-1)',
    colorName: 'color-3',
  },
};

/** Badge config for each level status. */
export const LEVEL_STATUS_CONFIG: Record<string, LevelStatusConfig> = {
  active: {
    label: 'Actif',
    icon: CheckCircle2,
    badgeVariant: 'active',
  },
  inactive: {
    label: 'Inactif',
    icon: XCircle,
    badgeVariant: 'outlined',
    badgeColor: 'rgb(var(--color-error))',
  },
};

/** Default level list filters. */
export const DEFAULT_LEVEL_FILTERS: LevelFilters = {
  cycles: [],
  statuses: [],
  searchTerm: undefined,
  minAge: undefined,
  maxAge: undefined,
};

/** Configure-panel order for level filter groups. */
export const ALL_FILTER_GROUP_IDS: FilterGroupId[] = [
  'filter-cycle',
  'filter-status',
  'filter-search',
  'filter-age-range',
];

/** Human-readable label per filter group (configure panel). */
export const FILTER_GROUP_LABELS: Record<FilterGroupId, string> = {
  'filter-cycle': 'Cycle',
  'filter-status': 'Statut',
  'filter-search': 'Recherche',
  'filter-age-range': "Tranche d'âge",
};

/** Returns the badge color name for a level status. */
export function getLevelStatusColor(status: string): 'success' | 'error' {
  return status === 'active' ? 'success' : 'error';
}

/** {@link Select} options for school cycle, including "none". */
export const LEVEL_CYCLE_FORM_OPTIONS = [
  { value: '', label: 'Aucun' },
  ...Object.entries(LEVEL_CYCLE_CONFIG).map(([value, item]) => ({
    value,
    label: item.label,
  })),
];

/** React Query keys invalidated after a level create / save. */
export const LEVEL_FORM_QUERY_KEYS = {
  list: ['levels'] as const,
  all: ['levels', 'all'] as const,
  one: (id: string) => ['level', id] as const,
};
